/* eslint-disable import/named */
import '../shared/styles/GlobalCSS.css'

import { NextPage } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { DefaultSeo } from 'next-seo'
import NextNProgress from 'nextjs-progressbar'
import { ReactElement, ReactNode, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { GlobalDialogProvider } from 'slices/Providers/GlobalDialogProvider/GlobalDialogProvider'
import { GlobalThemeProvider } from 'slices/Providers/GlobalThemeProvider/GlobalThemeProvider'

import { PixieWorldAuthContextProvider, usePixieWorldAuthContext } from '@/shared/auth/pixieWorldAuthContext'
import { CartProvider } from '@/shared/cart/CartContext'
import { CookieConsentProvider } from '@/shared/consent'
import { CookieConsentBanner } from '@/shared/consent/CookieConsentBanner'
import { CookieConsentDialog } from '@/shared/consent/CookieConsentDialog'
import { MarketProvider } from '@/shared/market'
import createEmotionCache from '@/shared/libs/createEmotionCache'
import { store, persistor } from '@/shared/store'
import { PersistGate } from 'redux-persist/integration/react'
import { CacheProvider, EmotionCache } from '@emotion/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { trackEvent, setUserId, setUserData, clearUserData } from '@/shared/analytics'

import { GlobalToastProvider } from '../slices/Providers/GlobalToastProvider/GlobalToastProvider'

function AnalyticsIdentify() {
  const { profile } = usePixieWorldAuthContext()

  useEffect(() => {
    if (profile?.id) {
      setUserId(String(profile.id))

      if (profile.email) {
        setUserData(profile.email)
      }
    } else {
      clearUserData()
    }
  }, [profile?.id, profile?.email])

  return null
}

const clientSideEmotionCache = createEmotionCache()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
})

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

export interface MyAppProps extends AppProps {
  Component: NextPageWithLayout
  emotionCache?: EmotionCache
}

// Wraps children in PersistGate only on the client, so SSR can render the
// full app tree. PersistGate renders its `loading` prop (null) during SSR
// because the persistor is not yet bootstrapped, which would otherwise
// blank out the page and break SEO (no head meta, no body content for
// crawlers). Persisted state (the editor slice) only matters on the client
// anyway — there's no localStorage on the server.
function ClientPersistGate({ children }: { children: ReactNode }) {
  const [hydrated, setHydrated] = useState(false)

  useEffect(() => {
    setHydrated(true)
  }, [])

  if (!hydrated) {
    return <>{children}</>
  }

  return (
    <PersistGate loading={null} persistor={persistor}>
      {children}
    </PersistGate>
  )
}

function MyApp({ Component, pageProps, emotionCache = clientSideEmotionCache }: MyAppProps) {
  const router = useRouter()
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page)

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      trackEvent('page_view', { page_path: url })
    }

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return (
    <CacheProvider value={emotionCache}>
      <Provider store={store}>
        <ClientPersistGate>
          <QueryClientProvider client={queryClient}>
            <PixieWorldAuthContextProvider>
              <AnalyticsIdentify />
              <MarketProvider>
                <CartProvider>
                  <NextNProgress color="#5B68E4" options={{ showSpinner: false }} />
                  <GlobalThemeProvider>
                    <CookieConsentProvider>
                      <GlobalToastProvider>
                        <GlobalDialogProvider>
                          <DefaultSeo
                            titleTemplate="%s - Pixie World"
                            defaultTitle="Pixie World - Personalised Storybooks"
                            openGraph={{
                              type: 'website',
                              site_name: 'Pixie World',
                              images: [
                                {
                                  url: 'https://www.pixieworldstore.com/static/og-image.png',
                                  width: 1200,
                                  height: 630,
                                  alt: 'Pixie World - Personalised Storybooks',
                                },
                              ],
                            }}
                            twitter={{
                              handle: '@pixieworldau',
                              site: '@pixieworldau',
                              cardType: 'summary_large_image',
                            }}
                          />
                          <Head>
                            <meta name="viewport" content="width=device-width, initial-scale=1" />
                          </Head>
                          {getLayout(<Component {...pageProps} />)}
                          <CookieConsentBanner />
                          <CookieConsentDialog />
                        </GlobalDialogProvider>
                      </GlobalToastProvider>
                    </CookieConsentProvider>
                  </GlobalThemeProvider>
                </CartProvider>
              </MarketProvider>
            </PixieWorldAuthContextProvider>
          </QueryClientProvider>
        </ClientPersistGate>
      </Provider>
    </CacheProvider>
  )
}

export default MyApp